import React, { useCallback, useEffect, useRef, useState } from 'react';
import { findingsAPI, incidentsAPI } from '../../api/traceability';
import { zonesAPI } from '../../api/client';
import {
  findingsForStage, canWork, rows, STAGES, FINDING_STATUS, INCIDENT_SEVERITIES, INCIDENT_STATUS, fetchAllRows,
} from '../../lib/traceability';
import { errorMessage } from '../../lib/evaluationIntegrity';
import { captureLocation } from '../../lib/fieldOperation';
import FindingCard, { EvidenceList } from './FindingCard';
import IncidentEvidence from './IncidentEvidence';
import ZoneSelector from '../ZoneSelector';
import { Field } from './Fields';

export default function Records({ event, user, stage = 'antes', incidents = false, onChanged }) {
  const [items, setItems] = useState([]);
  const [converted, setConverted] = useState({});
  const clientView = user?.role === 'cliente';
  const supportsNewRecords = typeof event.permissions?.can_close_incidents === 'boolean';
  useEffect(() => { setConverted({}); }, [event.id, user?.id]);
  const requestRef = useRef(0);
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('');
  const [filterStage, setFilterStage] = useState(stage);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const api = incidents ? incidentsAPI : findingsAPI;
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    try {
      const items = await fetchAllRows(api.list, { event_id: event.id,
        status: status || undefined, stage: incidents || filterStage === 'durante' ? undefined : filterStage || undefined });
      if (request !== requestRef.current) return;
      setItems(incidents ? items : findingsForStage(items, filterStage));
      setError('');
    } catch (err) {
      if (request === requestRef.current) setError(errorMessage(err, 'No se pudieron cargar los registros'));
    }
  }, [api, event.id, status, filterStage, incidents]);
  useEffect(() => { setFilterStage(stage); }, [stage]);
  // #Fase3-G8 — Un cambio de identidad no debe conservar hallazgos de la vista interna en el cliente.
  useEffect(() => {
    setItems([]);
    load();
  }, [load, user?.id, user?.role]);
  useEffect(() => {
    if (clientView) return;
    zonesAPI.list().then(({ data }) => setZones(rows(data))).catch(() => {
      setError('No se pudieron cargar las zonas. Recarga para reintentar.');
    });
  }, [clientView]);
  const run = async (operation) => {
    setBusy(true);
    try {
      await operation();
      setForm(null);
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const editable = canWork(user, event);
  return <section className="space-y-4">
    <h2 className="text-lg font-bold">{incidents ? 'Incidencias' : `Hallazgos de ${STAGES[stage]}`}</h2>
    {error && <p role="alert">{error} <button onClick={load}>Reintentar</button></p>}
    {!clientView && <Field label="Filtrar estatus" value={status} onChange={setStatus} options={{ '': 'Todos',
      ...(incidents ? INCIDENT_STATUS : FINDING_STATUS) }} />}
    {!clientView && !incidents && <Field label="Filtrar etapa" value={filterStage} onChange={setFilterStage}
      options={{ '': 'Todas', ...STAGES }} />}
    {editable && <button className="soem-btn-primary p-2" onClick={() => setForm({
      id: crypto.randomUUID(), captured_at: new Date().toISOString(), title: '', description: '',
      ...(!incidents ? { kind: 'hallazgo' } : {}), severity: incidents ? 'menor' : 'medio', stage: event.stage || undefined, status: 'abierto',
    })}>{incidents ? 'Nueva incidencia' : 'Nuevo hallazgo manual'}</button>}
    {editable && form && <form className="soem-card p-4 space-y-3" onSubmit={(e) => {
      e.preventDefault();
      if (form.kind === 'riesgo' && !supportsNewRecords) {
        setError('Para registrar nuevos riesgos, actualiza el servidor.');
        return;
      }
      run(async () => {
        const location = await captureLocation();
        await api.create({ ...form, event_id: event.id,
          project_id: event.project_id, location_id: event.location_id || event.location?.id || null,
          location, zone: form.zone?.id ? form.zone : null,
          ...(!incidents ? { origin: 'manual' } : {}),
        });
      });
    }}>
      {!incidents && supportsNewRecords && (form.stage || event.stage) === 'durante' && <Field label="Tipo de registro"
        value={form.kind} options={{ hallazgo: 'Hallazgo', riesgo: 'Nuevo riesgo' }}
        onChange={kind => setForm({ ...form, kind })} />}
      <Field label="Título" required value={form.title} onChange={(title) => setForm({ ...form, title })} />
      <Field label="Descripción" required multiline value={form.description}
        onChange={(description) => setForm({ ...form, description })} />
      <Field label="Severidad" value={form.severity} options={incidents ? INCIDENT_SEVERITIES : {
        critico: 'Crítico', alto: 'Alto', medio: 'Medio',
      }} onChange={(severity) => setForm({ ...form, severity })} />
      <Field label="Etapa" value={form.stage} options={{ '': 'Etapa actual del evento', ...STAGES }}
        onChange={(next) => setForm({ ...form, stage: next || undefined, kind: (next || event.stage) === 'durante' ? form.kind : 'hallazgo' })} />
      {!incidents && <Field label="Recomendación" value={form.recommendation} multiline
        onChange={(recommendation) => setForm({ ...form, recommendation })} />}
      <ZoneSelector zones={zones} zone={form.zone} onChange={(zone) => setForm({ ...form, zone })} />
      <button disabled={busy} className="soem-btn-primary p-2">Guardar</button>
      <button type="button" onClick={() => setForm(null)}>Cancelar</button>
    </form>}
    {!items.length && <p>Sin registros.</p>}
    {items.map((item) => incidents ? <article key={item.id} className="soem-card p-4 space-y-2">
      <h3 className="font-bold">{item.title}</h3><p>{item.description}</p>
      <p>{INCIDENT_STATUS[item.status]} · {item.zone?.label}</p>
      <EvidenceList items={item.evidence || []} />
      {editable && !supportsNewRecords && <p>No se pudo confirmar la disponibilidad de fotos de incidencias. Recarga el expediente para reintentar.</p>}
      {editable && supportsNewRecords && <IncidentEvidence incident={item} zones={zones} onChanged={async () => { await load(); onChanged?.(); }} />}
      {editable && event.permissions?.can_close_incidents !== false && <Field label="Estatus de incidencia" value={item.status} options={INCIDENT_STATUS}
        disabled={busy} onChange={(status) => run(() => incidentsAPI.update(item.id, { status }))} />}
      {(item.finding_id || converted[item.id]) ? <p>Convertida en hallazgo</p> : editable &&
        <button disabled={busy} className="soem-btn-secondary p-2" onClick={() => run(async () => {
          await incidentsAPI.toFinding(item.id);
          setConverted(current => ({ ...current, [item.id]: true }));
        })}>Convertir a hallazgo</button>}
    </article> : <div key={item.id}>
      {filterStage === 'durante' && item.stage === 'antes' && <p>Detectado en Antes</p>}
      <FindingCard finding={item} event={event} user={user} zones={zones}
      onChanged={async () => { await load(); onChanged?.(); }} /></div>)}
  </section>;
}
